import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../../../lib/api'
import sapConsumptionService from '../../../../../services/sapConsumptionService'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normaliza los campos de una línea de producción a un objeto con forma estable.
 * Devuelve null si faltan los campos mínimos requeridos (factory + prodLine).
 */
export function normalizeLineContext(selectedLine, selectedFactory) {
  if (!selectedLine) return null

  const factory =
    selectedLine.factory ||
    selectedLine.hierarchical_level?.[0] ||
    selectedFactory ||
    ''

  const prodLine = selectedLine.line || selectedLine.code || ''
  const sapCode = selectedLine.sap_code || selectedLine.code || selectedLine.line || ''
  const prodLineId =
    selectedLine.id ||
    selectedLine.ID ||
    selectedLine.code ||
    selectedLine.Code ||
    selectedLine.line ||
    selectedLine.sap_code ||
    ''

  if (!factory || !prodLine) return null
  return { factory, prodLine, sapCode, prodLineId }
}

/**
 * Normaliza el número de orden a un string estable.
 */
export function normalizeOrderNumber(selectedOrder) {
  return (selectedOrder?.OrderNumber || selectedOrder?.orderNumber || '').trim()
}

/**
 * Normaliza el ID del período activo.
 */
export function normalizePeriodID(activePeriod) {
  return activePeriod?.ID ?? activePeriod?.id ?? null
}

// ---------------------------------------------------------------------------
// Query keys — exportadas para poder invalidar desde mutations
// ---------------------------------------------------------------------------

export const queryKeys = {
  assetList: () => ['assetList'],

  sapOrders: (factory, prodLine, sapCode, fromSAP = false, orderFilter = '') =>
    ['sapOrders', { factory, prodLine, sapCode, fromSAP, orderFilter }],

  orderRecipe: (factory, prodLine, orderNumber, fromSAP = false) =>
    ['orderRecipe', { factory, prodLine, orderNumber, fromSAP }],

  orderPeriods: (factory, prodLine, orderNumber) =>
    ['orderPeriods', { factory, prodLine, orderNumber }],

  periodConsumptions: (periodID) =>
    ['periodConsumptions', { periodID }],

  consumptionsFallback: (factory, prodLine, orderNumber) =>
    ['consumptionsFallback', { factory, prodLine, orderNumber }],

  hopperPeriods: (factory, prodLine, orderNumber) =>
    ['hopperPeriods', { factory, prodLine, orderNumber }],

  dosingByLine: (prodLineId, orderNumber) =>
    ['dosingByLine', { prodLineId, orderNumber }],
}

// ---------------------------------------------------------------------------
// Hook 1: assets / líneas
// ---------------------------------------------------------------------------

/**
 * Carga la lista completa de assets.
 * El filtrado por fábrica/línea se hace en cliente con `select`.
 */
export function useAssetListQuery() {
  return useQuery({
    queryKey: queryKeys.assetList(),
    queryFn: async () => {
      const data = await api.get('/api/asset/list')
      return Array.isArray(data) ? data : []
    },
    staleTime: 5 * 60 * 1000, // 5 min — los assets no cambian frecuentemente
  })
}

// ---------------------------------------------------------------------------
// Hook 2: órdenes SAP por línea
// ---------------------------------------------------------------------------

/**
 * Carga las órdenes de producción para una línea concreta.
 * Aunque el endpoint es POST, conceptualmente es una lectura (query).
 *
 * @param {{ factory: string, prodLine: string, sapCode: string, fromSAP?: boolean }|null} ctx
 *   Resultado de normalizeLineContext(). Si es null la query queda deshabilitada.
 */
export function useSapOrdersQuery(ctx, fromSAP = false, orderFilter = 'pending') {
  const { factory, prodLine, sapCode } = ctx ?? {}
  return useQuery({
    queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, fromSAP, orderFilter),
    queryFn: async () => {
      const headers = {
        'Content-Type': 'application/json',
        Factory: factory,
        ProdLine: prodLine,
        SapCode: sapCode,
        SapRequest: fromSAP ? 'true' : 'false',
      }
      if (orderFilter) {
        headers.OrderFilter = orderFilter
      }
      const response = await api.post('/api/sap/orders', {}, { headers })
      return Array.isArray(response) ? response : []
    },
    enabled: !!(factory && prodLine && sapCode),
    staleTime: 30 * 1000, // 30 s
  })
}

// ---------------------------------------------------------------------------
// Hook 3: receta de orden
// ---------------------------------------------------------------------------

/**
 * Carga la receta (componentes) de una orden concreta.
 *
 * @param {{ factory: string, prodLine: string }|null} lineCtx
 * @param {string} orderNumber
 * @param {boolean} fromSAP  Si true fuerza recarga desde SAP
 */
export function useOrderRecipeQuery(lineCtx, orderNumber, fromSAP = false) {
  const { factory, prodLine } = lineCtx ?? {}
  return useQuery({
    queryKey: queryKeys.orderRecipe(factory, prodLine, orderNumber, fromSAP),
    queryFn: () =>
      sapConsumptionService.getOrderRecipe(factory, prodLine, orderNumber, fromSAP),
    enabled: !!(factory && prodLine && orderNumber),
    staleTime: 30 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Hook 4: períodos de la orden
// ---------------------------------------------------------------------------

/**
 * Carga los períodos de consumo de la orden seleccionada.
 *
 * @param {{ factory: string, prodLine: string }|null} lineCtx
 * @param {string} orderNumber
 */
export function useOrderPeriodsQuery(lineCtx, orderNumber) {
  const { factory, prodLine } = lineCtx ?? {}
  return useQuery({
    queryKey: queryKeys.orderPeriods(factory, prodLine, orderNumber),
    queryFn: async () => {
      const periods = await sapConsumptionService.getOrderPeriods(factory, prodLine, orderNumber)
      return Array.isArray(periods) ? periods : []
    },
    enabled: !!(factory && prodLine && orderNumber),
    staleTime: 15 * 1000,
    // 404 y "Registro no existe" son estados esperados (sin períodos aún); no deben bloquear la UI
    retry: (failureCount, error) => {
      if (
        error?.status === 404 ||
        error?.message?.includes('Not Found') ||
        error?.message?.includes('404') ||
        error?.message?.includes('Registro no existe')
      ) {
        return false
      }
      return failureCount < 2
    },
  })
}

// ---------------------------------------------------------------------------
// Hook 5: consumos del período activo
// ---------------------------------------------------------------------------

/**
 * Carga los consumos de un período concreto.
 *
 * @param {number|string|null} periodID  ID del período activo
 */
export function usePeriodConsumptionsQuery(periodID) {
  return useQuery({
    queryKey: queryKeys.periodConsumptions(periodID),
    queryFn: async () => {
      const data = await sapConsumptionService.getPeriodConsumptions(periodID)
      const filtered = (Array.isArray(data) ? data : []).filter(
        (c) => c && (c.ComponentSapCode || c.componentSapCode)?.trim()
      )
      return filtered
    },
    enabled: !!periodID,
    staleTime: 15 * 1000,
    retry: (failureCount, error) => {
      if (error?.status === 404 || error?.response?.status === 403) return false
      return failureCount < 2
    },
  })
}

// ---------------------------------------------------------------------------
// Hook 6: consumos fallback (sin período activo)
// ---------------------------------------------------------------------------

/**
 * Carga consumos usando el endpoint de fallback cuando no hay período activo.
 * Extrae los consumos de todos los períodos devueltos y los aplana en un array.
 *
 * @param {{ factory: string, prodLine: string }|null} lineCtx
 * @param {string} orderNumber
 * @param {boolean} hasPeriodID  Si hay período activo, esta query queda deshabilitada
 */
export function useConsumptionsFallbackQuery(lineCtx, orderNumber, hasPeriodID) {
  const { factory, prodLine } = lineCtx ?? {}
  return useQuery({
    queryKey: queryKeys.consumptionsFallback(factory, prodLine, orderNumber),
    queryFn: async () => {
      const periodsData = await sapConsumptionService.getConsumptionsFallback(
        factory,
        prodLine,
        orderNumber
      )
      const allConsumptions = (Array.isArray(periodsData) ? periodsData : []).flatMap(
        (period) => period.Consumptions || period.consumptions || []
      )
      return allConsumptions.filter(
        (c) => c && (c.ComponentSapCode || c.componentSapCode)?.trim()
      )
    },
    enabled: !!(factory && prodLine && orderNumber && !hasPeriodID),
    staleTime: 15 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Hook 7: períodos con consumos agrupados por hopper (para Cant. Declarada)
// ---------------------------------------------------------------------------

/**
 * Carga períodos con consumos agrupados por dosificador/hopper.
 * Se usa para calcular `declaredQuantitiesByComponent`.
 *
 * @param {{ factory: string, prodLine: string }|null} lineCtx
 * @param {string} orderNumber
 */
export function useHopperPeriodsQuery(lineCtx, orderNumber) {
  const { factory, prodLine } = lineCtx ?? {}
  return useQuery({
    queryKey: queryKeys.hopperPeriods(factory, prodLine, orderNumber),
    queryFn: () =>
      sapConsumptionService.getHopperConsumptionPeriods(factory, prodLine, orderNumber),
    enabled: !!(factory && prodLine && orderNumber),
    staleTime: 15 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Hook 8: dosificadores y hoppers por línea+orden
// ---------------------------------------------------------------------------

/**
 * Carga la configuración de dosificadores y hoppers para la línea+orden seleccionada.
 *
 * @param {{ prodLineId: string }|null} lineCtx
 * @param {string} orderNumber
 */
export function useDosingByLineQuery(lineCtx, orderNumber) {
  const { prodLineId } = lineCtx ?? {}
  return useQuery({
    queryKey: queryKeys.dosingByLine(prodLineId, orderNumber),
    queryFn: async () => {
      const response = await api.get('/api/asset/dosingbyline', {
        headers: {
          ProdLine_ID: String(prodLineId),
          SapOrderCode: orderNumber,
        },
      })

      // Normalizar distintas formas de respuesta del backend
      if (response == null) return []
      if (Array.isArray(response)) return response
      if (response?.data && Array.isArray(response.data)) return response.data
      if (typeof response === 'object') {
        const arr = Object.values(response).find(Array.isArray)
        return arr ?? []
      }
      return []
    },
    enabled: !!(prodLineId && orderNumber),
    staleTime: 2 * 60 * 1000, // 2 min
    retry: (failureCount, error) => {
      if (error?.status === 401 || error?.message?.includes('Unauthorized')) return false
      return failureCount < 2
    },
  })
}

// ---------------------------------------------------------------------------
// Hook utilitario: invalida todas las queries dependientes de una orden
// ---------------------------------------------------------------------------

/**
 * Devuelve una función que invalida en bloque todas las queries relacionadas
 * con la orden/período actual. Útil para refrescar todo tras cambios de estado.
 */
export function useInvalidateOrderData() {
  const queryClient = useQueryClient()

  return function invalidateOrderData({ factory, prodLine, sapCode, orderNumber, periodID } = {}) {
    const promises = []

    if (factory && prodLine && sapCode) {
      promises.push(
        queryClient.invalidateQueries({ queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, false, 'pending') }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sapOrders(factory, prodLine, sapCode, true, 'pending') })
      )
    }

    if (factory && prodLine && orderNumber) {
      promises.push(
        queryClient.invalidateQueries({ queryKey: queryKeys.orderRecipe(factory, prodLine, orderNumber, false) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orderRecipe(factory, prodLine, orderNumber, true) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orderPeriods(factory, prodLine, orderNumber) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.consumptionsFallback(factory, prodLine, orderNumber) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.hopperPeriods(factory, prodLine, orderNumber) })
      )
    }

    if (periodID) {
      promises.push(
        queryClient.invalidateQueries({ queryKey: queryKeys.periodConsumptions(periodID) })
      )
    }

    return Promise.all(promises)
  }
}
